import {Button, Col, Container, Form, InputGroup, Row, Spinner, Tab, Tabs, Toast, ToastBody} from "react-bootstrap";
import styles from "../usermanagement.module.css";
import {Formik} from "formik";
import * as yup from "yup";
import {useContext, useEffect, useState} from "react";
import {BackendContext} from "../../../context/Backend";
import {useNavigate, useRouteLoaderData} from "react-router";
import snakecaseKeys from "snakecase-keys";
import {useSubmit} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Eye, EyeSlash, EyeSlashFill} from "react-bootstrap-icons";
import "../../../App.css"
import {DelegationsTab} from "../DelegationsTab";

export const EditForm = ({user}) => {

    const backend = useContext(BackendContext);
    const navigate = useNavigate();
    const {currentUser} = useRouteLoaderData("root");
    const [countryList,setCountryList] = useState([]);
    const [languageList,setLanguageList] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const submit = useSubmit();
    const {t} = useTranslation();
    
    useEffect(()=>{
        setCountryList(backend.countries.map((val) => <option key={val.id} value={val.id}>{val.country}</option>));
    },[backend.countries])

    useEffect(()=>{
        setLanguageList(backend.languages.map((val) => <option key={val.id} value={val.id}>{val.language}</option>));
    },[backend.languages])

    const toggleEye = (e) => {
        setShowPassword(!showPassword);
    }

    const schema = yup.object().shape({
        tabProfile: yup.object().shape({
            firstName: yup.string().required(),
            lastName: yup.string().required(),
            address1: yup.string().optional(),
            address2: yup.string().optional(),
            address3: yup.string().optional(),
            company: yup.string().optional(),
            postalCode: yup.string().optional(),
            city: yup.string().optional(),
            state: yup.string().optional(),
            mobile: yup.string().matches(/^\+[0-9]{2} [1-9][0-9]+/,{message:t('profile.mobileFormat')}).optional()
        }),
        tabPassword: yup.object().shape(
            {
                password: yup.string().matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
                    {message: t('password.policy'), excludeEmptyString: true}),
                passwordRepeated: yup.string()
                    .when("password",
                        {
                            is: (val) => val,
                            then: (schema) =>
                                schema.required()
                                    .oneOf([yup.ref('password')], 'Your passwords do not match.'),
                        })
            }),
        credits: yup.number().min(0).max(currentUser.generalInfo.credits)
    });

    const handleCancel = (e) => {
        navigate("/");
    }

    return (<Formik
        validationSchema={schema}
        onSubmit={(values) => {
            const upUser = {
                ...user,
                uploadDocument: values.uploadDocument ? 1 : 0,
                deleteDocument: values.deleteDocument ? 1 : 0,
                createRequest: values.createRequest ? 1 : 0,
                createTemplate: values.createTemplate ? 1 : 0,
                websiteLogin: values.websiteLogin ? 1 : 0,
                syncFailed: values.syncFailed ? 1 : 0,
                syncPending: values.syncPending ? 1 : 0,
                syncFinished: values.syncFinished ? 1 : 0,
                firstName: values.tabProfile.firstName,
                lastName: values.tabProfile.lastName,
                company: values.tabProfile.company,
                street: values.tabProfile.address1,
                houseNumber: values.tabProfile.address2,
                addressLine3: values.tabProfile.address3,
                postalCode: values.tabProfile.postalCode,
                city: values.tabProfile.city,
                stateOrProvince: values.tabProfile.state,
                accountTypeId: values.creditPoolAccess ? 2 : 1,
                countryId: values.tabProfile.country,
                languageId: values.tabProfile.language,
                telephone: values.tabProfile.telephone,
                mobile: values.tabProfile.mobile
            };
            return new Promise((resolve) =>
                backend.api.updateUser(upUser, (resp) => {

                    if (values.credits > 0 && values.credits <= currentUser.generalInfo.credits) {
                        backend.api.addUserCredits({credits: values.credits, userId: user.userId}, (resp) => {
                            submit(null, {method: "post", action: "/"})
                            if (values.tabPassword.password === '') {
                                resolve();
                                navigate("/",{state:{message:t('system.saveSuccessful')}});
                            }
                        }, (error) => {
                            resolve();
                            navigate("/error", backend.api.getErrorState(error));
                        });
                    }

                    if (values.tabPassword.password !== '') {
                        backend.api.setUserPassword({
                            password: values.tabPassword.password,
                            userId: user.userId
                        }, (resp) => {
                            resolve();
                            navigate("/",{state:{message:t('system.saveSuccessful')}});
                        }, (error) => {
                            resolve();
                            navigate("/error", backend.api.getErrorState(error));
                        });
                    }

                    if (values.credits == 0 && values.tabPassword.password === '') {
                        submit(null, {method: "post", action: "/"})
                        resolve();
                        navigate("/",{state:{message:t('system.saveSuccessful')}});
                    }
                }, (error) => {
                }));

        }}
        initialValues={{
            uploadDocument: user.uploadDocument === 1,
            deleteDocument: user.deleteDocument === 1,
            createRequest: user.createRequest === 1,
            createTemplate: user.createTemplate === 1,
            websiteLogin: user.websiteLogin === 1,
            syncFailed: user.syncFailed === 1,
            syncPending: user.syncPending === 1,
            syncFinished: user.syncFinished === 1,
            creditPoolAccess: user.accountTypeId == 2,
            tabProfile: {
                firstName: user.firstName,
                lastName: user.lastName,
                company: user.company,
                address1: user.street,
                address2: user.houseNumber,
                address3: user.addressLine3,
                postalCode: user.postalCode,
                city: user.city,
                state: user.stateOrProvince,
                country: user.country?.id,
                language: user.languageId,
                telephone: user.telephone,
                mobile: user.mobile
            },
            tabPassword: {
                password: '',
                passwordRepeated: ""
            },
            credits: 0
        }}
    >
        {({
              handleSubmit, handleChange, values, touched,
              errors, isSubmitting, dirty, isValid
          }) => (
            <Form className={"p-0 " + styles.tabs} onSubmit={handleSubmit} autoComplete={"off"}>
                <Row>
                    <Tabs
                        defaultActiveKey="permissions"
                        transition={true}
                        id="edit-tab"
                        className={"mb-0 bg-primary"}
                    >
                        <Tab eventKey="permissions" title={t('permissions.header')} className={"small text-uppercase"}>
                            <Container fluid={true} className={"py-3"}>
                                <Row className={"mb-3"}>
                                    <Col>
                                        <Form.Check id={"perm-upload-document"} label={t('permissions.uploadDocument')} name={"uploadDocument"}
                                                    checked={values.uploadDocument} onChange={handleChange}/>
                                        <Form.Check id={"perm-delete-document"} label={t('permissions.deleteDocument')} name={"deleteDocument"}
                                                    checked={values.deleteDocument} onChange={handleChange}/>
                                        <Form.Check id={"perm-create-sig-request"} label={t('permissions.createSignatureRequest')}
                                                    name={"createRequest"}
                                                    checked={values.createRequest} onChange={handleChange}/>
                                        {/*<Form.Check id={"perm-create-template"} label={t('permissions.createTemplate')} name={"createTemplate"}*/}
                                        {/*            checked={values.createTemplate} onChange={handleChange}/>*/}
                                        <Form.Check id={"perm-login"} label={t('permissions.websiteLogin')} name="websiteLogin"
                                                    checked={values.websiteLogin} onChange={handleChange}/>
                                        <Form.Check id={"perm-direct-credit"} label={t('permissions.directCredit')} name={"creditPoolAccess"}
                                                    checked={values.creditPoolAccess} onChange={handleChange}/>
                                    </Col>
                                </Row>
                            </Container>
                        </Tab>
                        <Tab eventKey="synchronisation" title={t('synchronisation.header')} className={"small"}>
                            <Container fluid={true} className={"py-3"}>
                                <Row>
                                    <Col>
                                        <Form.Check name={"syncFailed"}
                                                    id={"sync-failed"}
                                                    label={t('synchronisation.syncFailed')}
                                                    checked={values.syncFailed} onChange={handleChange}
                                        />
                                        <Form.Check name={"syncPending"}
                                                    id={"sync-pending"}
                                                    label={t('synchronisation.syncPending')}
                                                    checked={values.syncPending} onChange={handleChange}
                                        />
                                        <Form.Check name={"syncFinished"}
                                                    id={"sync-finished"}
                                                    label={t('synchronisation.syncFinished')}
                                                    checked={values.syncFinished} onChange={handleChange}
                                        />
                                    </Col>
                                </Row>
                            </Container>
                        </Tab>
                        <Tab eventKey="profile" title={t('profile.header')}
                             tabClassName={!!errors.tabProfile ? ("text-danger") : null}>
                            <Container fluid={true} className={"py-4"}>
                                <Row>
                                    <Col>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.firstName"
                                                        className={styles.label}>{t('profile.firstName')}</Form.Label>
                                            <Form.Control type="text" name={"tabProfile.firstName"}
                                                          onChange={handleChange}
                                                          value={values.tabProfile.firstName}
                                                          isInvalid={!!errors.tabProfile?.firstName}/>
                                        </div>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.address1"
                                                        className={styles.label}>{t('profile.address1')}</Form.Label>
                                            <Form.Control type="text" name={"tabProfile.address1"}
                                                          onChange={handleChange}
                                                          value={values.tabProfile.address1}
                                                          isInvalid={!!errors.tabProfile?.address1}/>
                                        </div>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.address2"
                                                        className={styles.label}>{t('profile.address2')}</Form.Label>
                                            <Form.Control type="text" name={"tabProfile.address2"}
                                                          onChange={handleChange}
                                                          value={values.tabProfile.address2}
                                                          isInvalid={!!errors.tabProfile?.address2}/>
                                        </div>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.postalCode"
                                                        className={styles.label}>{t('profile.postalCode')}</Form.Label>
                                            <Form.Control type="text" name={"tabProfile.postalCode"}
                                                          onChange={handleChange}
                                                          value={values.tabProfile.postalCode}
                                                          isInvalid={!!errors.tabProfile?.postalCode}/>
                                        </div>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.telephone"
                                                        className={styles.label}>{t('profile.telephone')}</Form.Label>
                                            <Form.Control type="text" name={"tabProfile.telephone"}
                                                          onChange={handleChange}
                                                          value={values.tabProfile.telephone}
                                                          isInvalid={!!errors.tabProfile?.telephone}/>
                                        </div>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.state"
                                                        className={styles.label}>{t('profile.state')}</Form.Label>
                                            <Form.Control type="text" name={"tabProfile.state"} onChange={handleChange}
                                                          value={values.tabProfile.state}
                                                          isInvalid={!!errors.tabProfile?.state}/>
                                        </div>
                                        {/*<Form.Label htmlFor="inputTelephone"*/}
                                        {/*            className={styles.label}>Telephone</Form.Label>*/}
                                        {/*<Form.Control type="text" id={"inputTelephone"}/>*/}
                                    </Col>
                                    <Col>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.lastName"
                                                        className={styles.label}>{t('profile.lastName')}</Form.Label>
                                            <Form.Control type="text" name={"tabProfile.lastName"}
                                                          onChange={handleChange}
                                                          value={values.tabProfile.lastName}
                                                          isInvalid={!!errors.tabProfile?.lastName}/>
                                        </div>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.address3"
                                                        className={styles.label}>{t('profile.address3')}</Form.Label>
                                            <Form.Control type="text" name={"tabProfile.address3"}
                                                          onChange={handleChange}
                                                          value={values.tabProfile.address3}
                                                          isInvalid={!!errors.tabProfile?.address3}/>
                                        </div>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.company"
                                                        className={styles.label}>{t('profile.company')}</Form.Label>
                                            <Form.Control type="text" name={"tabProfile.company"}
                                                          onChange={handleChange}
                                                          value={values.tabProfile.company}
                                                          isInvalid={!!errors.tabProfile?.company}/>
                                        </div>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.city"
                                                        className={styles.label}>{t('profile.city')}</Form.Label>
                                            <Form.Control type="text" name={"tabProfile.city"} onChange={handleChange}
                                                          value={values.tabProfile.city}
                                                          isInvalid={!!errors.tabProfile?.city}/>
                                        </div>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.mobile"
                                                        className={styles.label}>{t('profile.mobile')}</Form.Label>
                                            <Form.Control type="text" name={"tabProfile.mobile"} onChange={handleChange}
                                                          value={values.tabProfile.mobile}
                                                          isInvalid={!!errors.tabProfile?.mobile}/>
                                            <Form.Control.Feedback className={"px-2"}
                                                type={"invalid"}>{errors.tabProfile?.mobile}</Form.Control.Feedback>
                                        </div>

                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.country"
                                                        className={styles.label}>{t('profile.country')}</Form.Label>
                                            <Form.Select
                                                name={"tabProfile.country"}
                                                value={values.tabProfile.country}
                                                onChange={handleChange}>
                                                {countryList}
                                            </Form.Select>
                                        </div>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabProfile.language"
                                                        className={styles.label}>{t('profile.language')}</Form.Label>
                                            <Form.Select
                                                name={"tabProfile.language"}
                                                value={values.tabProfile.language}
                                                onChange={handleChange}>
                                                {languageList}
                                            </Form.Select>
                                        </div>
                                    </Col>
                                </Row>
                            </Container>
                        </Tab>
                        <Tab eventKey="password" title={t('password.header')}
                             tabClassName={!!errors.tabPassword ? ("text-danger") : null}>
                            <Container fluid={true} className={"my-4"}>
                                <Row>
                                    <Col>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabPassword.password"
                                                        className={styles.label}>{t('password.newPassword')}</Form.Label>
                                            <InputGroup>
                                                <Form.Control name="tabPassword.password"
                                                              type={showPassword ? ("text") : ("password")}
                                                              value={values.tabPassword.password}
                                                              autoComplete={"new-password"}
                                                              isInvalid={!!errors.tabPassword?.password}
                                                              onChange={handleChange}/>
                                                <InputGroup.Text className={"bg-light border-0"}>{showPassword ?
                                                    <EyeSlash onClick={toggleEye}/> :
                                                    <Eye onClick={toggleEye}/>}</InputGroup.Text>
                                                <Form.Control.Feedback
                                                    type={"invalid"} className={"px-2"}>{errors.tabPassword?.password}</Form.Control.Feedback>
                                            </InputGroup>
                                        </div>
                                    </Col>
                                    <Col>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="tabPassword.passwordRepeated"
                                                        className={styles.label}>{t('password.repeated')}</Form.Label>
                                            <Form.Control name="tabPassword.passwordRepeated" type="password"
                                                          autoComplete={"new-password"}
                                                          isInvalid={!!errors.tabPassword?.passwordRepeated}
                                                          value={values.tabPassword.passwordRepeated}
                                                          onChange={handleChange}/>
                                        </div>
                                    </Col>
                                </Row>
                            </Container>
                        </Tab>
                        <Tab eventKey="credits" title={t('credits.header')}>
                            <Container fluid={true} className={"my-4"}>
                                <Row xs={"auto"} className={"justify-content-center"}>
                                    <Col>
                                        <div className={"inputContainer"}>
                                            <Form.Label htmlFor="credits" className={styles.label}>{t('credits.add')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="credits"
                                                className={styles.creditInput}
                                                onChange={handleChange}
                                                value={values.credits}
                                                isInvalid={!!errors.credits}
                                            />
                                            <Form.Text className={"px-2"} id="creditHelpBlock" muted>
                                                {t('credits.header')}: {user.credits}
                                            </Form.Text>
                                        </div>
                                    </Col>
                                </Row>
                            </Container>
                        </Tab>
                        <Tab eventKey="delegates" title={t('delegates.title')}>
                            <DelegationsTab userId={user.userId}/>
                        </Tab>
                    </Tabs>
                </Row>
                <Row>
                    <div className="d-flex gap-2 p-2 justify-content-center">
                        <Button className={"websobutton"} size={"md"} variant={"danger"}
                                onClick={handleCancel}>{t('system.cancelButton')}</Button>
                        <Button className={"websobutton"} type={"submit"} disabled={isSubmitting || !dirty || !isValid} size={"md"}>
                            {isSubmitting ? (<Spinner
                                className={"me-2"}
                                as="span"
                                animation="border"
                                size="sm"
                            />) : null}{t('system.saveButton')}</Button>
                    </div>
                </Row>
            </Form>
        )}
    </Formik>);
}